import React, { useState } from 'react';
import { isAxiosError } from 'axios';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { useContainer } from 'unstated-next';
import moment from 'moment';

import i18n from '../../../i18n/i18n';
import GoogleAutocomplete from '../../../components/GoogleAutocomplete';
import { GlobalStateContainer } from '../../../provider/GlobalStateContainer';

import auth from '../../../utils/auth';
import { AuthModalMode, Currency } from '../../../utils/constants';
import { enquireParking } from '../../../services/apiService';
import useToasts from '../../../hooks/useToasts';

const EnquireParkingModal = ({
  isModalOpen,
  onCloseModal,
}) => {
  const { toastSuccess, toastError } = useToasts();
  const { setAuthModalState } = useContainer(GlobalStateContainer);
  const authenticated = Boolean(auth.getRefreshToken());
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [startDate, setStartDate] = useState("");
  const [currency, setCurrency] = useState(Currency.MY);
  const [estBudget, setEstBudget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const checkIsNotEmpty = (value) => {
    if (value === '' || value === undefined || value == null) {
      toastError(i18n.t('findParking.enquireParkingForm.errorMessage'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    if (!authenticated) {
      setSubmitting(false);
      setAuthModalState({ isModalOpen: true, mode: AuthModalMode.LOGIN });
      return;
    }

    try {
      if (
        checkIsNotEmpty(address) &&
        checkIsNotEmpty(startDate) &&
        checkIsNotEmpty(estBudget)
      ) {
        await enquireParking({
          company,
          address,
          estBudget,
          startDate: moment(startDate).format('YYYY-MM-DD'),
          currency,
        });
        toastSuccess(i18n.t('findParking.enquireParkingForm.successMessage'));
        onCloseModal();
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toastError(error.response.data.message);
      } else {
        toastError(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div tabIndex="-1" aria-hidden="true" className={`fixed top-0 left-0 right-0 bottom-0 z-50 w-full p-4 overflow-x-hidden overflow-y-auto max-h-screen ${isModalOpen ? '' : 'hidden'}`}>
      <div className="flex items-center justify-center min-h-screen text-center max-w-[80%] md:max-w-[500px] mx-auto">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-900 opacity-75" />
        </div>
        <div className="relative bg-white z-20 rounded-lg shadow">
          <div className="flex items-center justify-between p-4 border-b rounded-t">
            <h3 className="text-xl font-semibold text-gray-900">{i18n.t('findParking.enquireParkingForm.title')}</h3>
            <button type="button" className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center" onClick={onCloseModal}>
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          <div className="px-8 pt-3 pb-8 min-w-[500px]">
            <div className="flex flex-col gap-4 text-left">
              <p className="text-gray-900 mb-2">{i18n.t('findParking.enquireParkingForm.description')}</p>
              <div className="flex flex-col gap-2">
                <p className="text-[13px]">{i18n.t('findParking.enquireParkingForm.location')}</p>
                <GoogleAutocomplete
                  placeholder={i18n.t('findParking.enquireParkingForm.locationPlaceholder')}
                  onPlaceChanged={(place) => {
                    if (place.formatted_address.includes("Malaysia")) {
                      setCurrency(Currency.MY);
                    } else if (place.formatted_address.includes("Hong Kong")) {
                      setCurrency(Currency.HK);
                    } else if (place.formatted_address.includes("Singapore")) {
                      setCurrency(Currency.SG);
                    }

                    setAddress(`${place.name}${place.formatted_address ? ", " + place.formatted_address : ""}`);
                  }}
                  className="w-full h-[42px] rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[13px]">{i18n.t('findParking.enquireParkingForm.parkingForWork')}</p>
                <input
                  type="text"
                  onChange={(e) => setCompany(e.target.value)}
                  value={company}
                  placeholder={i18n.t('findParking.enquireParkingForm.parkingForWorkPlaceholder')}
                  className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[13px]">{i18n.t('findParking.enquireParkingForm.whenStartParking')}</p>
                <DatePicker
                  selected={startDate}
                  value={startDate}
                  minDate={new Date()}
                  onChange={date => setStartDate(date)}
                  placeholderText="dd/mm/yy"
                  autoComplete="off"
                  className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[13px]">{i18n.t('findParking.enquireParkingForm.estimatedBudget')} ({currency})</p>
                <input
                  required
                  type="number"
                  onChange={(e) => setEstBudget(e.target.value)}
                  value={estBudget}
                  min={1}
                  placeholder={i18n.t('findParking.enquireParkingForm.estimatedBudgetPlaceholder')}
                  className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end border-t border-gray-200 p-6 space-x-2 rounded-b">
            <button onClick={onCloseModal} type="button" disabled={submitting} className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none rounded-lg text-sm font-medium px-5 py-2.5 disabled:opacity-50">{i18n.t('findParking.filterFields.cancel')}</button>
            <button onClick={handleSubmit} type="button" disabled={submitting} className="text-white bg-primary900 hover:bg-primary focus:ring-4 focus:outline-none font-medium rounded-[24px] text-sm px-5 py-2.5 text-center disabled:opacity-50">{i18n.t('findParking.enquireParkingForm.submit')}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EnquireParkingModal;
